import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Database, Download, Upload, Mail, Trash2, AlertTriangle, ShieldAlert, Clock } from "lucide-react";

function ExportarCard() {
  const download = () => {
    // usa location.href para forçar download com cookie auth
    window.location.href = `${API}/backup/export`;
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base text-[#083CA6] flex items-center gap-2">
          <Download className="w-5 h-5" />Exportar Backup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">
          Baixa um arquivo SQLite com todos os dados do sistema: inscrições, usuários (com senhas criptografadas),
          empreendimentos, cards informativos e configurações.
        </p>
        <Button onClick={download} className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-exportar-backup">
          <Download className="w-4 h-4 mr-2" />Baixar SQLite
        </Button>
      </CardContent>
    </Card>
  );
}

function ImportarCard({ onRestored }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/backup/collections")
      .then((r) => {
        setCollections(r.data.collections);
        setSelected(new Set(r.data.collections));
      })
      .catch(() => {});
  }, []);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const toggle = (c) => {
    const next = new Set(selected);
    if (next.has(c)) next.delete(c); else next.add(c);
    setSelected(next);
  };

  const restaurar = async () => {
    if (!file) return toast.error("Selecione um arquivo primeiro");
    if (selected.size === 0) return toast.error("Selecione ao menos uma coleção");
    const isAll = selected.size === collections.length;
    if (!window.confirm(
      `Restaurar ${isAll ? "TODAS as coleções" : `apenas: ${Array.from(selected).join(", ")}`}?\n\n` +
      "Registros do backup serão reinseridos ou atualizados por ID."
    )) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (!isAll) fd.append("collections", Array.from(selected).join(","));
      const r = await api.post("/backup/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Backup restaurado");
      onRestored?.(r.data.restaurado);
      setFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao restaurar");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base text-[#083CA6] flex items-center gap-2">
          <Upload className="w-5 h-5" />Importar Backup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">
          Selecione o arquivo SQLite e marque quais coleções deseja restaurar. Upsert por ID —
          registros atuais que não estão no backup são preservados.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept=".sqlite,.db,application/x-sqlite3,application/octet-stream"
          onChange={pickFile}
          className="hidden"
          data-testid="input-backup-file"
        />
        <div className="flex gap-2 items-center flex-wrap">
          <Button
            onClick={() => inputRef.current?.click()}
            variant="outline"
            className="border-[#083CA6] text-[#083CA6]"
            data-testid="btn-selecionar-backup"
          >
            <Upload className="w-4 h-4 mr-2" />Selecionar SQLite
          </Button>
          {file && <span className="text-xs text-slate-600 truncate max-w-[240px]">{file.name}</span>}
        </div>

        {collections.length > 0 && (
          <div>
            <div className="text-xs text-slate-600 mb-2 flex items-center justify-between">
              <span>Coleções para restaurar:</span>
              <div className="flex gap-2">
                <button
                  className="text-[#083CA6] hover:underline"
                  onClick={() => setSelected(new Set(collections))}
                  data-testid="btn-selecionar-todas"
                >Todas</button>
                <span>·</span>
                <button
                  className="text-[#083CA6] hover:underline"
                  onClick={() => setSelected(new Set())}
                  data-testid="btn-limpar-selecao"
                >Nenhuma</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {collections.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm p-2 rounded border hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.has(c)}
                    onChange={() => toggle(c)}
                    data-testid={`coll-${c}`}
                  />
                  <span>{c}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <Button
          onClick={restaurar}
          disabled={uploading || !file || selected.size === 0}
          className="bg-[#00883A] hover:bg-[#006e2e]"
          data-testid="btn-restaurar"
        >
          {uploading ? "Restaurando..." : `Restaurar ${selected.size} coleção(ões)`}
        </Button>
      </CardContent>
    </Card>
  );
}

function EmailBackupCard() {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/backup/config");
      setEmail(r.data.email || "");
    } catch (e) {
      logger.error("Falha ao carregar config", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const salvar = async () => {
    setSaving(true);
    try {
      await api.put("/backup/config", { email });
      toast.success("E-mail de backup salvo");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  const enviar = async () => {
    setSending(true);
    try {
      const r = await api.post("/backup/enviar-email");
      if (r.data.ok) toast.success(`Backup enviado para ${r.data.email_destino} (${(r.data.size_bytes/1024).toFixed(1)} KB)`);
      else toast.error(`Falha: ${r.data.detalhes?.error || "erro desconhecido"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSending(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base text-[#083CA6] flex items-center gap-2">
          <Mail className="w-5 h-5" />Backup automático por e-mail
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">
          Configure um e-mail para receber o backup em anexo. Ao clicar em <strong>Enviar agora</strong>,
          o sistema gera um SQLite atualizado e envia via Resend.
        </p>
        <div className="grid md:grid-cols-[1fr_auto_auto] gap-2 items-end">
          <div>
            <Label htmlFor="backup-email" className="text-xs">E-mail de recuperação</Label>
            <Input
              id="backup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@grajau.ma.gov.br"
              data-testid="input-backup-email"
            />
          </div>
          <Button onClick={salvar} disabled={saving} variant="outline" data-testid="btn-salvar-backup-email">
            {saving ? "Salvando..." : "Salvar"}
          </Button>
          <Button onClick={enviar} disabled={sending || !email} className="bg-[#00883A] hover:bg-[#006e2e]" data-testid="btn-enviar-backup-email">
            <Mail className="w-4 h-4 mr-2" />{sending ? "Enviando..." : "Enviar agora"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ZerarCard({ onZeroed }) {
  const [open1, setOpen1] = useState(false); // primeira confirmação
  const [open2, setOpen2] = useState(false); // segunda confirmação
  const [cpf, setCpf] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setCpf(""); setSenha(""); setOpen1(false); setOpen2(false); };

  const primeiro = () => {
    if (!cpf || !senha) return toast.error("Preencha CPF e Senha");
    setOpen1(true);
  };

  const simPrimeiro = () => { setOpen1(false); setOpen2(true); };

  const executar = async () => {
    setLoading(true);
    try {
      const r = await api.post("/backup/zerar-inscricoes", {
        cpf, senha,
        confirmacao_1: true,
        confirmacao_2: true,
      });
      toast.success(`${r.data.removidas} inscrição(ões) removidas. Backup de segurança salvo.`);
      reset();
      onZeroed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao zerar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className="border-red-200 bg-red-50/30">
        <CardHeader>
          <CardTitle className="text-base text-red-700 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" />Zerar Banco de Inscrições
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-700">
            <strong>Ação destrutiva!</strong> Remove <strong>TODAS as inscrições</strong> do sistema.
            Um backup de segurança é gerado automaticamente antes da exclusão.
          </p>
          <p className="text-sm text-slate-600">
            Informe seu CPF e senha do Super Admin para autorizar:
          </p>
          <div className="grid md:grid-cols-2 gap-2">
            <div>
              <Label htmlFor="zerar-cpf" className="text-xs">CPF</Label>
              <Input
                id="zerar-cpf"
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                placeholder="000.000.000-00"
                data-testid="zerar-cpf"
              />
            </div>
            <div>
              <Label htmlFor="zerar-senha" className="text-xs">Senha</Label>
              <Input
                id="zerar-senha"
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                data-testid="zerar-senha"
              />
            </div>
          </div>
          <Button
            onClick={primeiro}
            variant="destructive"
            disabled={loading}
            data-testid="btn-zerar-inscricoes"
          >
            <Trash2 className="w-4 h-4 mr-2" />Zerar banco de inscrições
          </Button>
        </CardContent>
      </Card>

      {/* Primeira confirmação */}
      <Dialog open={open1} onOpenChange={setOpen1}>
        <DialogContent data-testid="dialog-conf-1">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-6 h-6" />Realmente deseja excluir o banco de dados?
            </DialogTitle>
            <DialogDescription>
              Todas as inscrições serão removidas permanentemente. Um backup de segurança será salvo
              automaticamente antes da exclusão.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen1(false)} data-testid="btn-nao-1">Não</Button>
            <Button variant="destructive" onClick={simPrimeiro} data-testid="btn-sim-1">Sim</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Segunda confirmação */}
      <Dialog open={open2} onOpenChange={setOpen2}>
        <DialogContent data-testid="dialog-conf-2">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-6 h-6" />Deseja prosseguir?
            </DialogTitle>
            <DialogDescription>
              Esta é a última confirmação. Ao clicar em <strong>Continuar</strong>, a operação será
              executada imediatamente e não poderá ser desfeita (exceto restaurando o backup).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen2(false)} data-testid="btn-cancelar-2" disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={executar}
              disabled={loading}
              data-testid="btn-continuar-2"
            >
              {loading ? "Executando..." : "Continuar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AgendarBackupCard() {
  const [cfg, setCfg] = useState({ frequencia: "disabled", hora: 3, dia_semana: 0, next_run: null });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/backup/schedule");
      setCfg(r.data);
    } catch (e) {
      logger.error("Falha ao carregar agendamento", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const salvar = async () => {
    setSaving(true);
    try {
      const r = await api.put("/backup/schedule", cfg);
      setCfg(r.data);
      toast.success("Agendamento atualizado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  const dias = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base text-[#083CA6] flex items-center gap-2">
          <Clock className="w-5 h-5" />Backup agendado automático
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">
          Configure o sistema para gerar e enviar o backup automaticamente para o e-mail cadastrado
          acima. O horário é em <strong>UTC</strong> (subtraia 3h para horário de Brasília).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Frequência</Label>
            <select
              className="w-full border rounded px-3 py-2 text-sm mt-1"
              value={cfg.frequencia}
              onChange={(e) => setCfg({ ...cfg, frequencia: e.target.value })}
              data-testid="agendar-frequencia"
            >
              <option value="disabled">Desativado</option>
              <option value="diario">Diário</option>
              <option value="semanal">Semanal</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Hora (UTC)</Label>
            <select
              className="w-full border rounded px-3 py-2 text-sm mt-1"
              value={cfg.hora}
              onChange={(e) => setCfg({ ...cfg, hora: Number(e.target.value) })}
              disabled={cfg.frequencia === "disabled"}
              data-testid="agendar-hora"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Dia da semana</Label>
            <select
              className="w-full border rounded px-3 py-2 text-sm mt-1"
              value={cfg.dia_semana}
              onChange={(e) => setCfg({ ...cfg, dia_semana: Number(e.target.value) })}
              disabled={cfg.frequencia !== "semanal"}
              data-testid="agendar-dia"
            >
              {dias.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <Button onClick={salvar} disabled={saving} className="bg-[#00883A] hover:bg-[#006e2e]" data-testid="btn-salvar-agenda">
            {saving ? "Salvando..." : "Salvar agendamento"}
          </Button>
        </div>
        {cfg.next_run && (
          <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-2 mt-2">
            <strong>Próxima execução:</strong> {new Date(cfg.next_run).toLocaleString("pt-BR")} ·
            {" "}<span className="font-mono">{new Date(cfg.next_run).toISOString()}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function BancoDeDadosPage() {
  return (
    <div className="space-y-6" data-testid="banco-dados-page">
      <div>
        <h1 className="text-3xl font-black text-[#083CA6] flex items-center gap-3">
          <Database className="w-8 h-8" />Banco de Dados
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Backup, restauração e reset do banco de dados do sistema
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ExportarCard />
        <ImportarCard />
      </div>

      <EmailBackupCard />
      <AgendarBackupCard />

      <ZerarCard />
    </div>
  );
}
